import * as Phaser from 'phaser';
import { LightSource } from '../entities/LightSource';
import { Mirror } from '../entities/Mirror';
import { SwitchEntity } from '../entities/Switch';
import { Exit } from '../entities/Exit';
import { markHowToPlaySeen } from '../progress';
import { LanguageDropdown } from './LanguageDropdown';
import { bindText, onLangChange } from '../i18n/helpers';
import { isRTL } from '../i18n';
import type { TranslationKey } from '../i18n/types';
import { FONT, PALETTE, TYPE } from '../render/palette';
import { addAtmosphere } from '../render/atmosphere';
import { responsiveFontSize, ensureTouchHitArea } from './responsive-font';

const ICON = 48;

export class HowToPlayScene extends Phaser.Scene {
  private st!: {
    TITLE: Phaser.Types.GameObjects.Text.TextStyle;
    HEADING: Phaser.Types.GameObjects.Text.TextStyle;
    BODY: Phaser.Types.GameObjects.Text.TextStyle;
    HINT: Phaser.Types.GameObjects.Text.TextStyle;
    LABEL: Phaser.Types.GameObjects.Text.TextStyle;
    ARROW: Phaser.Types.GameObjects.Text.TextStyle;
  };

  constructor() {
    super('how-to-play');
  }

  create(): void {
    addAtmosphere(this, 0.7);

    this.st = {
      TITLE: {
        color: FONT.TITLE,
        fontFamily: TYPE.SANS,
        fontSize: `${responsiveFontSize(this, 26)}px`,
        fontStyle: '400',
      },
      HEADING: {
        color: FONT.TITLE,
        fontFamily: TYPE.SANS,
        fontSize: `${responsiveFontSize(this, 16)}px`,
        fontStyle: '500',
      },
      BODY: {
        color: FONT.TEXT,
        fontFamily: TYPE.SANS,
        fontSize: `${responsiveFontSize(this, 15)}px`,
      },
      HINT: {
        color: FONT.DIM,
        fontFamily: TYPE.SANS,
        fontSize: `${responsiveFontSize(this, 12)}px`,
      },
      LABEL: {
        color: FONT.DIM,
        fontFamily: TYPE.MONO,
        fontSize: `${responsiveFontSize(this, 11)}px`,
      },
      ARROW: {
        color: FONT.DIM,
        fontFamily: TYPE.SANS,
        fontSize: `${responsiveFontSize(this, 20)}px`,
      },
    };

    const titleText = this.add.text(480, 50, '', this.st.TITLE).setOrigin(0.5);
    titleText.setLetterSpacing(8);
    bindText(titleText, 'howto.title', this);

    const pageText = this.add.text(480, 86, '', this.st.HINT).setOrigin(0.5);
    pageText.setLetterSpacing(3);
    bindText(pageText, 'howto.page', this, { n: 1, total: 2 });

    const piecesHeading = this.add.text(480, 130, '', this.st.HEADING).setOrigin(0.5);
    piecesHeading.setLetterSpacing(5);
    bindText(piecesHeading, 'howto.pieces.heading', this);
    this.legend(190);

    const controlsHeading = this.add.text(480, 300, '', this.st.HEADING).setOrigin(0.5);
    controlsHeading.setLetterSpacing(5);
    bindText(controlsHeading, 'howto.controls.heading', this);
    this.actions(380);
    bindText(this.add.text(480, 472, '', this.st.HINT).setOrigin(0.5), 'howto.controls.hint', this);

    bindText(
      this.add.text(480, 514, '', this.st.BODY).setOrigin(0.5),
      'howto.objective.line1',
      this,
    );
    bindText(
      this.add.text(480, 538, '', this.st.HINT).setOrigin(0.5),
      'howto.objective.line2',
      this,
    );

    const back = this.add
      .triangle(0, 660, 0, 10, 18, 0, 18, 20, PALETTE.HUD_DIM)
      .setInteractive({ useHandCursor: true });
    back.on('pointerover', () => back.setFillStyle(PALETTE.HUD_TITLE));
    back.on('pointerout', () => back.setFillStyle(PALETTE.HUD_DIM));
    back.on('pointerdown', () => {
      markHowToPlaySeen();
      this.scene.start('menu');
    });

    const fsNext = responsiveFontSize(this, 13);
    const next = this.add
      .text(0, 660, '', {
        color: FONT.DIM,
        fontFamily: TYPE.SANS,
        fontSize: `${fsNext}px`,
        fontStyle: '500',
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    ensureTouchHitArea(next, this, Phaser.Geom.Rectangle);
    next.setLetterSpacing(3);
    next.on('pointerover', () => next.setColor(FONT.TITLE));
    next.on('pointerout', () => next.setColor(FONT.DIM));
    next.on('pointerdown', () => {
      markHowToPlaySeen();
      this.scene.start('how-to-play-color');
    });
    bindText(next, 'nav.nextPage', this);

    const placeChrome = (): void => {
      if (isRTL()) {
        back.x = 820;
        back.setRotation(Math.PI);
        next.x = 140;
      } else {
        back.x = 140;
        back.setRotation(0);
        next.x = 820;
      }
    };
    placeChrome();
    onLangChange(this, placeChrome);

    new LanguageDropdown(this);
  }

  private legend(y: number): void {
    const items: Array<[TranslationKey, () => Phaser.GameObjects.GameObject]> = [
      ['howto.pieces.source', () => new LightSource(this, ICON, 0, 0, 'E')],
      [
        'howto.pieces.mirror',
        () => new Mirror({ scene: this, cellSize: ICON, gridX: 0, gridY: 0 }),
      ],
      ['howto.pieces.switch', () => buildSwitchPair(this, ICON, [])],
      [
        'howto.pieces.exit',
        () => {
          const exit = new Exit(this, ICON, 0, 0);
          exit.setUnlocked(true);
          return exit;
        },
      ],
      [
        'howto.pieces.wall',
        () => {
          const gfx = this.add.graphics();
          gfx.fillStyle(PALETTE.WALL, 1);
          gfx.fillRoundedRect(-ICON / 2, -ICON / 2, ICON, ICON, 3);
          gfx.lineStyle(1, PALETTE.WALL_SHADOW, 1);
          gfx.strokeRoundedRect(-ICON / 2, -ICON / 2, ICON, ICON, 3);
          gfx.fillStyle(PALETTE.WALL_HIGHLIGHT, 0.5);
          gfx.fillRect(-ICON / 2 + 2, -ICON / 2 + 2, ICON - 4, 1);
          return gfx;
        },
      ],
    ];
    const xs = [180, 320, 460, 600, 740];
    items.forEach(([key, make], i) => {
      const obj = make() as unknown as { setPosition(x: number, y: number): void };
      obj.setPosition(xs[i]!, y);
      const label = this.add.text(xs[i]!, y + 44, '', this.st.LABEL).setOrigin(0.5);
      label.setLetterSpacing(2);
      bindText(label, key, this);
    });
  }

  private actions(y: number): void {
    this.slot(240 - 30, y);
    this.add.text(240, y, '→', this.st.ARROW).setOrigin(0.5);
    new Mirror({
      scene: this,
      cellSize: ICON,
      gridX: 0,
      gridY: 0,
      initialAngleDeg: 45,
    }).setPosition(240 + 30, y);
    this.actionLabel(240, y, 'howto.controls.clickSlot.line1', 'howto.controls.clickSlot.line2');

    new Mirror({
      scene: this,
      cellSize: ICON,
      gridX: 0,
      gridY: 0,
      initialAngleDeg: 30,
    }).setPosition(480 - 30, y);
    this.add.text(480, y, '→', this.st.ARROW).setOrigin(0.5);
    new Mirror({
      scene: this,
      cellSize: ICON,
      gridX: 0,
      gridY: 0,
      initialAngleDeg: 105,
    }).setPosition(480 + 30, y);
    this.actionLabel(480, y, 'howto.controls.dragMirror.line1', 'howto.controls.dragMirror.line2');

    new Mirror({
      scene: this,
      cellSize: ICON,
      gridX: 0,
      gridY: 0,
      initialAngleDeg: 45,
    }).setPosition(720 - 30, y);
    this.add.text(720, y, '→', this.st.ARROW).setOrigin(0.5);
    this.slot(720 + 30, y);
    this.actionLabel(720, y, 'howto.controls.rightClick.line1', 'howto.controls.rightClick.line2');
  }

  private actionLabel(x: number, y: number, key1: TranslationKey, key2: TranslationKey): void {
    bindText(this.add.text(x, y + 38, '', this.st.BODY).setOrigin(0.5, 0), key1, this);
    bindText(this.add.text(x, y + 60, '', this.st.HINT).setOrigin(0.5, 0), key2, this);
  }

  private slot(x: number, y: number): void {
    const g = this.add.graphics();
    g.fillStyle(PALETTE.SLOT_FILL, 0.6);
    g.fillRoundedRect(x - 22, y - 22, 44, 44, 6);
    g.lineStyle(1, PALETTE.SLOT_FRAME_EMPTY, 0.4);
    g.strokeRoundedRect(x - 22, y - 22, 44, 44, 6);
  }
}

/**
 * Build an off → arrow → on Switch pair, centred on local (0, 0). Used by
 * the how-to-play screens to demonstrate the lit/unlit states of each switch
 * variant in a single inline icon. `requires` selects the hue (empty array =
 * slice-1 generic switch).
 */
export function buildSwitchPair(
  scene: Phaser.Scene,
  cellSize: number,
  requires: ReadonlyArray<'R' | 'G' | 'B'>,
): Phaser.GameObjects.Container {
  const offset = cellSize * 0.48;
  const off = new SwitchEntity(scene, cellSize, 0, 0, 'demo-off', requires);
  off.setPosition(-offset, 0);
  const on = new SwitchEntity(scene, cellSize, 0, 0, 'demo-on', requires);
  on.setPosition(offset, 0);
  on.setLit(true);
  const arrow = scene.add.graphics();
  arrow.setAlpha(0.45);
  arrow.lineStyle(0.8, 0xdddddd, 1);
  arrow.lineBetween(-6, 0, 5, 0);
  arrow.fillStyle(0xdddddd, 1);
  arrow.fillTriangle(5, -2.5, 5, 2.5, 8, 0);
  return scene.add.container(0, 0, [off, arrow, on]);
}
